/**
 * ContactPage — direct contact form for The Shared Origins Global
 * Network.
 *
 * Apr 30 2026 — Lead's call: "white and black hard on the eye." Reverted
 * the previous light theme to the canonical dark cinematic theme.
 * Form inputs now use a dark obsidian fill with cyan focus rings, gold
 * primary accents, and white/secondary text. Functionality unchanged.
 */
import { useState } from 'react';
import { Mail, Send, CheckCircle2, AlertTriangle, ScrollText } from 'lucide-react';
import api from '../lib/api';
import PageNarration from './PageNarration';

const CATEGORIES = [
  { value: 'correction', label: 'Source correction' },
  { value: 'methodology', label: 'Methodology question' },
  { value: 'partnership', label: 'Partnership / press' },
  { value: 'bug', label: 'Bug report' },
  { value: 'other', label: 'Other' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', category: 'correction', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorText, setErrorText] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      setStatus('error');
      setErrorText('Email and message are required.');
      return;
    }
    setStatus('sending');
    setErrorText('');
    try {
      await api.post('/contact/inbound', form);
      setStatus('sent');
      setForm({ name: '', email: '', category: 'correction', subject: '', message: '' });
    } catch (err) {
      const isRateLimit = err?.response?.status === 429;
      if (isRateLimit) {
        setStatus('error');
        setErrorText('Too many messages from this connection in the last 5 minutes. Wait a moment, or email us directly at sales@midniteproductions.org.');
        return;
      }
      const subject = encodeURIComponent(form.subject || `[TSOGN] ${form.category}`);
      const body = encodeURIComponent(`From: ${form.name || 'Anonymous'} <${form.email}>\nCategory: ${form.category}\n\n${form.message}`);
      window.location.href = `mailto:sales@midniteproductions.org?subject=${subject}&body=${body}`;
      setStatus('sent');
    }
  };

  // Common dark-themed input class used across the form.
  const inputCls = "w-full bg-black/40 border border-[#2F8A92]/20 rounded px-3 py-2.5 font-mono text-sm text-white placeholder:text-white/35 focus:border-[#2F8A92]/70 focus:outline-none transition-colors";

  return (
    <div className="px-4 md:px-8 lg:px-12 py-10 md:py-14 max-w-4xl mx-auto" data-testid="contact-page">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <header className="mb-10 md:mb-14">
        <div className="flex items-start justify-between gap-4 mb-3">
          <p className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#2F8A92]/85" data-testid="contact-eyebrow">
            Direct line to the builder
          </p>
          <PageNarration pageKey="contact" label="Listen · Contact" />
        </div>
        <h1 className="font-historical text-[#D4A84A] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] mb-5" data-testid="contact-headline">
          Got a source we missed?
        </h1>
        <p className="font-historical text-white/85 text-lg md:text-xl leading-relaxed max-w-2xl">
          A correction. A real concern. A peer-reviewed paper we should be citing. Tell us. A real
          human reads every message — usually within 24 hours.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-10">
        {/* ── Form ─────────────────────────────────────────────────── */}
        <form onSubmit={submit} className="md:col-span-3 space-y-4" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[#2F8A92]/80 mb-1.5">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Optional"
                className={inputCls}
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[#2F8A92]/80 mb-1.5">Email <span className="text-[#D4A84A]">*</span></label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@somewhere.com"
                className={inputCls}
                data-testid="contact-email"
              />
            </div>
          </div>

          <div>
            <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[#2F8A92]/80 mb-1.5">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className={inputCls}
              data-testid="contact-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value} style={{ background: '#0A0A0A' }}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[#2F8A92]/80 mb-1.5">Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder={`[${form.category}] Brief one-line subject`}
              className={inputCls}
              data-testid="contact-subject"
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[#2F8A92]/80 mb-1.5">Message <span className="text-[#D4A84A]">*</span></label>
            <textarea
              required
              rows={7}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Sources welcome. Specific references make it 10× faster to verify and act on."
              className={`${inputCls} resize-y`}
              data-testid="contact-message"
            />
          </div>

          {status === 'error' && (
            <div className="border border-red-500/40 bg-red-500/10 rounded px-3 py-2.5 flex items-start gap-2" data-testid="contact-error">
              <AlertTriangle size={14} className="text-red-400 mt-0.5" />
              <p className="font-mono text-xs text-red-300">{errorText}</p>
            </div>
          )}
          {status === 'sent' && (
            <div className="border border-[#2F8A92]/40 bg-[#2F8A92]/10 rounded px-3 py-2.5 flex items-start gap-2" data-testid="contact-success">
              <CheckCircle2 size={14} className="text-[#2F8A92] mt-0.5" />
              <p className="font-mono text-xs text-[#2F8A92]">Message sent. A real human will read it — usually within 24 hours.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#D4A84A]/55 bg-[#D4A84A]/10 hover:bg-[#D4A84A]/20 text-[#D4A84A] font-mono text-sm tracking-widest uppercase transition-colors disabled:opacity-50"
            data-testid="contact-submit"
          >
            <Send size={14} />
            {status === 'sending' ? 'Sending…' : 'Send message'}
          </button>
        </form>

        {/* ── Sidebar ──────────────────────────────────────────────── */}
        <aside className="md:col-span-2 space-y-5">
          <div className="border border-[#D4A84A]/30 rounded-md p-6 md:p-8" data-testid="contact-direct" style={{ background: 'rgba(212,168,74,0.04)' }}>
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D4A84A]/85 mb-3 inline-flex items-center gap-2">
              <Mail size={12} /> Direct
            </p>
            <a
              href="mailto:sales@midniteproductions.org"
              className="block font-mono text-sm md:text-base text-[#2F8A92] hover:text-[#D4A84A] transition-colors leading-snug break-all"
              data-testid="contact-direct-email"
            >
              sales@midniteproductions.org
            </a>
          </div>

          <div className="border border-white/10 rounded-md p-4" data-testid="contact-about">
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/55 mb-2">About this address</p>
            <p className="font-mono text-xs text-white/65 leading-relaxed">
              Routes directly to the builder&apos;s inbox. Not a no-reply address. Not an autoresponder. Reply
              expected within 24 hours during the contest window.
            </p>
          </div>

          <div className="border-l-2 border-[#2F8A92]/40 pl-3" data-testid="contact-policy">
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#2F8A92]/85 mb-1.5 inline-flex items-center gap-1.5">
              <ScrollText size={11} /> Correction policy
            </p>
            <p className="font-mono text-xs text-white/65 leading-relaxed">
              Sourced corrections take priority. Include a peer-reviewed citation, a primary source, or
              a museum / archive accession number and we&apos;ll review the chain inside one publication
              cycle. Methodology obligates us to do so — see About Us → &ldquo;evidence log is sovereign&rdquo;.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
